import PersonIcon from "@mui/icons-material/Person";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { normalizeEmployeeCalendarColor } from "../../../staff/employee/addemployee/employeeFormShared";
import {
  bookingSourceStyles,
  calendarSlotFill,
  formatAppointmentPurpose,
  formatPatientTypeLabel,
  formatPreAppointmentStatus,
  formatQueueFromItem,
  isLightCssColor,
  patientMobileFromItem,
  patientRegNoFromItem,
  patientTypeLabelFromItem,
  PATIENT_TYPE_STYLES,
  preAppointmentStatusFromItem,
  purposeColorBackground,
  purposeColorFromItem,
} from "../../viewappointment/components/appointmentViewShared";

const CALENDAR_TEXT = "#334155";
const CALENDAR_MUTED = "#64748b";
/** Light indigo when purpose and doctor colour are both missing. */
const DEFAULT_ACCENT = "#c7d2fe";

/** Soft pink for completed calendar slots. */
const COMPLETED_SLOT_BG = "#fce7f3";
const COMPLETED_SLOT_BORDER = "#f9a8d4";
const COMPLETED_SLOT_TEXT = "#9d174d";

/** Shared day-view column grid (header + event rows). */
export const DAY_VIEW_COLUMN_GRID =
  "grid w-full min-w-0 items-center gap-x-2 [grid-template-columns:minmax(9rem,1.5fr)_3.25rem_4.5rem_6rem_minmax(5.5rem,1.2fr)_5.75rem_3.25rem_auto]";

export function DayViewColumnHeader() {
  const cell =
    "text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate";
  return (
    <div className="border-b border-slate-200/80 bg-slate-50/95 px-2 py-1.5">
      <div className={`${DAY_VIEW_COLUMN_GRID} pl-0`}>
        <span className={cell}>Patient</span>
        <span className={cell}>Type</span>
        <span className={cell}>Reg No</span>
        <span className={cell}>Phone</span>
        <span className={cell}>Purpose</span>
        <span className={cell}>Session / Room</span>
        <span className={`${cell} text-center`}>Token</span>
        <span className={`${cell} text-right`}>View</span>
      </div>
    </div>
  );
}

function calendarEventColors(
  appointment: any,
  colorByPurpose?: boolean,
  isCompleted?: boolean
) {
  if (isCompleted) {
    return {
      bg: COMPLETED_SLOT_BG,
      border: COMPLETED_SLOT_BORDER,
      accent: COMPLETED_SLOT_TEXT,
      text: COMPLETED_SLOT_TEXT,
      muted: COMPLETED_SLOT_TEXT,
    };
  }

  const purposeColor = purposeColorFromItem(appointment);
  const doctorColor =
    normalizeEmployeeCalendarColor(appointment?.color) || appointment?.color;
  const accent =
    colorByPurpose && purposeColor
      ? purposeColor
      : doctorColor || DEFAULT_ACCENT;
  const light = isLightCssColor(accent);

  return {
    bg: calendarSlotFill(accent, 22),
    border: purposeColorBackground(accent, light ? 70 : 48),
    accent: light ? CALENDAR_TEXT : accent,
    text: CALENDAR_TEXT,
    muted: CALENDAR_MUTED,
  };
}

function sessionRoomLabel(appointment: any): string {
  const session = String(appointment?.sessionName ?? "").trim();
  const room = String(appointment?.roomNumber ?? "").trim();
  if (session && room) return `${session} · Rm ${room}`;
  if (session) return session;
  if (room) return `Room ${room}`;
  return "";
}

export default function AppointmentEvent({
  appointment,
  isMonthView,
  isDayView,
  colorByPurpose,
  hideDoctorName,
  alignColumns,
  reschedule,
  onOnlineAppointmentClick,
}: {
  reschedule?: any;
  appointment: any;
  isMonthView?: boolean;
  isDayView?: boolean;
  /** When a doctor is selected, color slots by purpose instead of doctor color. */
  colorByPurpose?: boolean;
  /** Hide dentist on the slot (day view already shows the doctor column). */
  hideDoctorName?: boolean;
  /** Day view + one doctor: line name / type / phone / purpose up in columns. */
  alignColumns?: boolean;
  onOnlineAppointmentClick?: any;
}) {
  const isCompleted = appointment?.status === "completed";
  const { bg, border, accent, text, muted } = calendarEventColors(
    appointment,
    colorByPurpose,
    isCompleted
  );
  const isDefault = appointment?.type === "default" && appointment?.type;
  const isOnline = appointment?.type === "online";
  const purpose = formatAppointmentPurpose(appointment);
  const showPurpose = !isMonthView && purpose && purpose !== "N/A";
  const phone = patientMobileFromItem(appointment);
  const showPhone = Boolean(phone && phone !== "—");
  const patientTypeLabel = patientTypeLabelFromItem(appointment);
  const patientTypeShort = formatPatientTypeLabel(patientTypeLabel);
  const showDoctor =
    !hideDoctorName && !isMonthView && Boolean(appointment?.dentist);
  const overlapCount = Number(appointment?._overlapCount || 0);
  const showOverlapBadge = isDayView && overlapCount > 0;
  const patientRegNo = patientRegNoFromItem(appointment);
  const showRegNo = Boolean(patientRegNo && patientRegNo !== "—");
  const tokenNo = formatQueueFromItem(appointment);
  const showToken = Boolean(tokenNo && tokenNo !== "0");
  const bookedKey = preAppointmentStatusFromItem(appointment);
  const bookedLabel = formatPreAppointmentStatus(bookedKey);
  const sessionRoom = sessionRoomLabel(appointment);

  const metaParts = [
    showPhone ? phone : null,
    showPurpose ? purpose : null,
    showDoctor ? appointment?.dentist : null,
  ].filter(Boolean) as string[];

  const handleClick = () => {
    if (isOnline && !!onOnlineAppointmentClick && !appointment?.added) {
      onOnlineAppointmentClick(appointment);
    } else {
      reschedule(appointment);
    }
  };

  const title = [
    appointment?.patientName,
    bookedLabel || null,
    showRegNo ? patientRegNo : null,
    showPhone ? phone : null,
    patientTypeShort,
    showPurpose ? purpose : null,
    sessionRoom || null,
    showToken ? `#${tokenNo}` : null,
    showDoctor ? appointment?.dentist : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const dividerCell =
    "min-w-0 truncate border-l border-black/10 pl-2 first:border-l-0 first:pl-0";

  return (
    <div
      onClick={handleClick}
      className="box-border h-full w-full cursor-pointer overflow-hidden rounded-md px-2 py-0.5"
      style={{
        backgroundColor: bg,
        color: text,
        border: isDefault ? "none" : `1px solid ${border}`,
      }}
      title={title}
    >
      {isDayView && !isMonthView ? (
        alignColumns ? (
          <div className={`${DAY_VIEW_COLUMN_GRID} h-full`}>
            <div className={`${dividerCell} flex min-w-0 flex-col justify-center gap-0.5`}>
              <span
                className="flex min-w-0 items-center gap-1 truncate text-[13px] font-semibold leading-tight"
                style={{ color: text }}
              >
                <PersonIcon
                  sx={{ fontSize: 15, color: accent }}
                  className="shrink-0 opacity-90"
                />
                <span className="truncate">{appointment?.patientName || ""}</span>
              </span>
              {bookedLabel ? (
                <span
                  className={`inline-flex w-fit max-w-full truncate rounded border px-1.5 py-px text-[9px] font-semibold leading-none ${
                    bookingSourceStyles[bookedKey] || bookingSourceStyles.default
                  }`}
                  title={`Booked · ${bookedLabel}`}
                >
                  Booked · {bookedLabel}
                </span>
              ) : null}
            </div>
            <span
              className={`${dividerCell} inline-flex h-5 w-full items-center justify-center rounded px-1 text-[10px] font-bold uppercase tracking-wide border leading-none ${
                PATIENT_TYPE_STYLES[patientTypeLabel] ||
                PATIENT_TYPE_STYLES["Existing Patient"]
              }`}
            >
              {patientTypeShort}
            </span>
            <span
              className={`${dividerCell} text-[13px] font-semibold tabular-nums`}
              style={{ color: muted }}
              title={showRegNo ? patientRegNo : undefined}
            >
              {showRegNo ? patientRegNo : "—"}
            </span>
            <span
              className={`${dividerCell} text-[13px] font-semibold tabular-nums`}
              style={{ color: muted }}
            >
              {showPhone ? phone : "—"}
            </span>
            <span
              className={`${dividerCell} text-[13px] font-semibold`}
              style={{ color: muted }}
              title={showPurpose ? purpose : undefined}
            >
              {showPurpose ? purpose : "—"}
            </span>
            <span
              className={`${dividerCell} text-[12px] font-semibold`}
              style={{ color: muted }}
              title={sessionRoom || undefined}
            >
              {sessionRoom || "—"}
            </span>
            <span
              className={`${dividerCell} text-center text-[13px] font-bold tabular-nums`}
              style={{ color: text }}
              title={showToken ? `Token ${tokenNo}` : "No token"}
            >
              {showToken ? tokenNo : "—"}
            </span>
            <span className={`${dividerCell} inline-flex shrink-0 items-center justify-end gap-1`}>
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-white/80 text-indigo-600 hover:bg-white"
                title="View appointment"
                aria-label="View appointment"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
              </button>
              {showOverlapBadge ? (
                <span
                  className="inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600"
                  title={`Overlaps with ${overlapCount} more appointment${
                    overlapCount === 1 ? "" : "s"
                  }`}
                >
                  +{overlapCount}
                </span>
              ) : null}
            </span>
          </div>
        ) : (
          <div className="flex h-full min-w-0 items-center gap-2 overflow-hidden">
            <PersonIcon
              sx={{ fontSize: 16, color: accent }}
              className="shrink-0 opacity-90"
            />
            <span
              className="shrink-0 truncate text-[14px] font-semibold max-w-[9.5rem]"
              style={{ color: text }}
            >
              {appointment?.patientName || ""}
            </span>
            <span
              className={`inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[11px] font-bold uppercase tracking-wide border leading-none ${
                PATIENT_TYPE_STYLES[patientTypeLabel] ||
                PATIENT_TYPE_STYLES["Existing Patient"]
              }`}
            >
              {patientTypeShort}
            </span>
            {bookedLabel ? (
              <span
                className={`inline-flex shrink-0 truncate rounded border px-1.5 py-0.5 text-[10px] font-semibold ${
                  bookingSourceStyles[bookedKey] || bookingSourceStyles.default
                }`}
              >
                {bookedLabel}
              </span>
            ) : null}
            {showPhone ? (
              <span
                className="shrink-0 text-[14px] font-bold tabular-nums"
                style={{ color: muted }}
              >
                {phone}
              </span>
            ) : null}
            {showPurpose || showDoctor ? (
              <span
                className="min-w-0 truncate text-[14px] font-bold"
                style={{ color: muted }}
              >
                {[showPurpose ? purpose : null, showDoctor ? appointment?.dentist : null]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            ) : null}
            {showOverlapBadge ? (
              <span
                className="ml-auto inline-flex shrink-0 items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[11px] font-semibold text-slate-600"
                title={`Overlaps with ${overlapCount} more appointment${
                  overlapCount === 1 ? "" : "s"
                }`}
              >
                +{overlapCount}
              </span>
            ) : null}
          </div>
        )
      ) : (
        <div className="flex flex-col justify-center gap-0 h-full min-h-0 leading-tight">
          <div className="flex items-center gap-1 min-w-0 shrink-0">
            <PersonIcon
              sx={{ fontSize: 11, color: accent }}
              className="shrink-0 opacity-90"
            />
            <span
              className="text-[11px] font-semibold truncate min-w-0 flex-1"
              style={{ color: text }}
            >
              {appointment?.patientName}
            </span>
            {!isMonthView ? (
              <span
                className={`inline-flex items-center rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide border shrink-0 leading-none ${
                  PATIENT_TYPE_STYLES[patientTypeLabel] ||
                  PATIENT_TYPE_STYLES["Existing Patient"]
                }`}
              >
                {patientTypeShort}
              </span>
            ) : null}
          </div>

          {!isMonthView && metaParts.length > 0 ? (
            <div
              className="min-w-0 truncate text-[9px] font-medium"
              style={{ color: muted }}
            >
              {metaParts.join(" · ")}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
